<template>
    <div :class="{'flex flex-col justify-start items-center h-screen overflow-y-auto pb-16' : notSmallScreen}">
        <div class="grid grid-cols-4 max-w-lg gap-4 w-full">
            <!--Beneficiary Payment Reference-->
            <div class="col-span-4 md:col-span-2">
                <text-input
                    input-label="Source Account Reference"
                    placeholder="INV998877"
                    v-model="form.payment_reference"
                    :has-errors="form.errors.has('payment_reference')"
                ></text-input>
                <template v-slot="errorMessage">
                    <p v-if="form.errors.has('payment_reference')"
                       class="mt-1 text-xs text-red-600 leading-tight">{{ form.errors.get("payment_reference") }}
                    </p>
                </template>
            </div>

            <!--Beneficiary Selection-->
            <div class="col-span-4 mb-6 pr-4">
                <label class="flex justify-start items-end truncate">
                    <span class="block text-sm font-medium leading-5 text-gray-700">Select RAMS Beneficiary (Optional)</span>
                </label>

                <remote-select
                    ref="selectContactInput"
                    v-model="selectedBeneficiary"
                    @focus="fetchBeneficiaries"
                    :remote-method="fetchBeneficiaries"
                    :loading="loadingBeneficiaries"
                    :data="beneficiarySearchResults"
                    item-label="name"
                    placeholder="Type to search current beneficiaries">
                    <template slot-scope="props">
                        <div class="flex item-center justify-between py-2">
                            <div class="font-medium text-gray-800">{{ props.item.name }}</div>
                            <div class="flex items-center justify-end">
                                <div class="mr-4 rounded overflow-hidden">
                                    <img class="w-8 h-8" v-if="bankIconPath(props.item.bank) != null" :src="bankIconPath(props.item.bank)">
                                    <span v-else class="text-gray-400 font-medium text-xs">{{ props.item.bank }}</span>
                                </div>
                                <span class="text-indigo-600 font-medium">{{ props.item.account_number }}</span>
                            </div>
                        </div>
                    </template>
                </remote-select>
            </div>

            <!--Transaction Description-->
            <div class="col-span-4">
                <text-input
                    v-model="form.description"
                    placeholder="Waterproofing Kitchen Ceiling"
                    input-label="Transaction Description"
                    :has-errors="form.errors.has('description')">
                </text-input>
                <template v-slot="errorMessage">
                    <p v-if="form.errors.has('description')"
                       class="mt-1 text-xs text-red-600 leading-tight">{{ form.errors.get("description") }}
                    </p>
                </template>
            </div>

            <!--VAT Type-->
            <div class="col-span-2">
                <label class="block text-sm font-medium leading-5 text-gray-700">VAT Type</label>

                <select v-model="form.vat_type"
                        class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring focus:ring-blue-400 focus:border-blue-300 transition duration-150 ease-in-out text-sm leading-5">
                    <template v-if="hasFeature('release_vat_updates')">
                        <option v-for="vatType in vatTypes" :key="vatType.key" :value="vatType.value" :label="vatType.key"></option>
                    </template>
                    <template v-else>
                        <option v-for="vatType in vatTypes" :key="vatType.key" :value="vatType.key" :label="vatType.key | titlecase"></option>
                    </template>
                </select>

            </div>
            <div class="hidden md:block col-span-2"></div>

            <!--Total-->
            <div class="col-span-4 md:col-span-2">
                <text-input
                    input-label="Invoice Total (Incl.)"
                    placeholder="2500.25"
                    v-model="form.total"
                    :has-errors="form.errors.has('total')">
                </text-input>

                <template v-slot="errorMessage">
                    <p v-if="form.errors.has('total')"
                       class="mt-1 text-xs text-red-600 leading-tight">{{ form.errors.get("total") }}
                    </p>
                </template>
            </div>

            <!--VAT-->
            <div class="col-span-4 md:col-span-2" v-if="form.vat_type !== 'no_vat' && form.vat_type !== '0'">
                <text-input
                    input-label="VAT"
                    placeholder="150.00"
                    v-model="form.vat"
                    :disabled="form.vat_type !== 'custom'"
                    :has-errors="form.errors.has('vat')">
                </text-input>

                <template v-slot="errorMessage">
                    <p v-if="form.errors.has('vat')"
                       class="mt-1 text-xs text-red-600 leading-tight">{{ form.errors.get("vat") }}
                    </p>
                </template>
            </div>

            <!-- Supporting Documents-->
            <div class="col-span-4 mb-6">
                <label class="block text-sm font-medium leading-5 text-gray-700">Supporting Document</label>
                <div v-if="form.supporting_documents && form.supporting_documents.length > 0">
                    <div v-for="document in form.supporting_documents" class="flex items-center justify-between">
                        <div>{{ document.name }}</div>
                        <button @click.prevent="removeFile(document, false)" class="py-1" :disabled="form.busy">
                            <icon-trash class="h-4 w-4 text-gray-400 hover:text-brand transition duration-300"></icon-trash>
                        </button>
                    </div>
                </div>

                <div class="col-span-4 grid grid-cols-2 gap-4">
                    <div v-if="form.supporting_documents && form.supporting_documents.length === 0"
                         class="col-span-2 lg:col-span-1 w-full flex justify-center hover:bg-indigo-50 border border-dotted border-gray-300 rounded-md transition duration-300 font-medium outline-none p-6">
                        <button class="text-indigo-600 font-medium" @click.prevent="toggleMultiAttachmentModal">Select from ticket documents</button>
                    </div>
                    <vue-dropzone v-show="form.supporting_documents && form.supporting_documents.length === 0"
                                  ref="documentDropzone"
                                  class="transition ease-in-out duration-100 col-span-2 lg:col-span-1"
                                  :use-custom-slot="true"
                                  :include-styling="false"
                                  id="dropzone"
                                  :options="dropzoneOptions"
                                  @vdropzone-drag-over="dragOver"
                                  @vdropzone-drag-leave="dragLeave"
                                  @vdropzone-error="showDropzoneError"
                                  @vdropzone-error-multiple="showDropzoneError"
                                  @vdropzone-file-added="fileAdded"
                                  @vdropzone-max-files-exceeded="removeFile">
                        <button
                            class="dropzone-container w-full flex justify-center hover:bg-indigo-50 hover:text-indigo-800 border border-dotted border-gray-300 rounded-md transition duration-300 font-medium text-indigo-600 outline-none p-6">
                            Click or drag here to upload new document
                        </button>
                    </vue-dropzone>
                </div>
            </div>

            <multi-upload-component
                v-if="showMultiUploadModal"
                :show="showMultiUploadModal"
                :filter-options="['ticket', 'email']"
                pre-applied-filter="ticket"
                :media-url="multiUploadUrl()"
                :full-width="true"
                submit-label="Attach"
                additional-body-classes="mx-12"
                @media-selected="attachSelectedMedia"
                @close-modal="toggleMultiAttachmentModal()">
            </multi-upload-component>

            <!--Bill to account selection-->
            <div class="col-span-4 md:col-span-2 mb-6">
                <label class="block text-sm font-medium leading-5 text-gray-700">Bill to account</label>
                <select id="bill_to_select"
                        v-model="form.bill_to"
                        class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring focus:ring-blue-400 focus:border-blue-300 transition duration-150 ease-in-out text-sm leading-5">
                    <option v-for="billTo in billedToList" :value="billTo" :label="billTo | titlecase"></option>
                </select>
            </div>

            <checkbox v-if="form.bill_to === 'landlord'" class="col-span-4 md:col-span-2 flex items-center"
                      v-model="form.recover_from_tenant" label="Recover From Tenant"
                      :disabled="form.bill_to === 'tenant'">
            </checkbox>
            <div v-else class="hidden sm:flex sm:col-span-2"></div>

            <div class="col-span-4 flex justify-end space-x-2 mt-2">

                <button type="button"
                        @click.prevent="closeForm()"
                        :disabled="form.busy"
                        class="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                    Cancel
                </button>

                <secondary-button @click.prevent="editOrCreate()"
                                  :disabled="form.busy">
                    {{ submitButtonText }}
                </secondary-button>
            </div>
        </div>
    </div>
</template>

<script>
import bankListMixin from "../../mixins/bank-list";
import DropzoneOptions from "../../mixins/dropzone-options";
import VatInput from "../../mixins/vat-input";

export default {
    name: "TransactionRequestCreateUpdateForm",

    mixins: [bankListMixin, DropzoneOptions, VatInput],

    props: {
        transactionRequest: {
            required: false,
            type: Object,
            description: "The transaction request we are editing",
            default: null,
        },
        parentModel: {
            required: true,
            type: Object,
            description: "Used to pass in the ticket/inspection so we can resolve the rams asset external source id and integration id"
        },
        assetId: {
            required: false,
            description: "Used when creating a transaction request",
            default: null,
        },

        leaseId: {
            required: false,
            description: "Used when creating a transaction request",
            default: null,
        },

        contactGroupId: {
            required: false,
            description: "Used when creating a transaction request",
            default: null,
        },

        ticketId: {
            required: false,
            description: "Used to specify the ticket this request originated from",
            default: null
        },

        inspectionId: {
            required: false,
            description: "Used to specify the inspection this request originated from",
            default: null
        }
    },

    data () {
        return {
            mutableTransactionRequest: this.transactionRequest,
            form: new AppForm({}),
            documentUploadLimit: 1,
            beneficiarySearchResults: [],
            selectedBeneficiary: null,
            loadingBeneficiaries: false,
            showMultiUploadModal: false,
            attachments: []
        };
    },

    computed: {
        billedToList () {
            let array = ["landlord", "asset group creditors"];
            if (this.contactGroupId) {
                array.unshift("tenant");
            }
            return array;
        },
        submitButtonText () {
            if (this.mutableTransactionRequest && this.form.busy) {
                return "Updating...";
            } else if (this.mutableTransactionRequest) {
                return "Update";
            } else if (this.form.busy) {
                return "Submitting...";
            } else {
                return "Submit Request";
            }
        },
    },

    watch: {
        "form.bill_to": function (person) {
            if (person === "tenant") {
                this.form.recover_from_tenant = false;
            }
        },
        selectedBeneficiary: function (newValue) {
            this.form.beneficiary_details = newValue;
            this.form.beneficiary_id = (newValue && newValue.id) ? newValue.id : null;
        }
    },

    mounted () {
        this.mutableTransactionRequest = this.transactionRequest;
        this.setupForm();

        if (this.mutableTransactionRequest && this.mutableTransactionRequest.beneficiary_details != null) {
            this.selectedBeneficiary = this.mutableTransactionRequest.beneficiary_details;
        }

        // when not editing we need the leaseId and assetId
        if (!this.mutableTransactionRequest) {
            if (!this.parentModel || !this.parentModel.rams_asset_external_source) {
                toastr.error("Billing requests can only be made on RAMS assets");
                this.closeForm();
                return;
            } else {
                this.form.contact_group_id = this.contactGroupId;
                this.form.lease_id = this.leaseId;
            }

            if (!this.assetId) {
                this.closeForm();
                toastr.error("Asset required to create billing request");
            } else {
                this.form.asset_id = this.assetId;
            }
        }
    },

    methods: {
        setupForm () {
            let defaultVatRate = this.hasFeature("release_vat_updates") ? this.state.vat_percentages.find(vat => vat.key.includes("default")).value : "15_percent_rate";

            this.form = new AppForm({
                category_name: this.mutableTransactionRequest ? this.mutableTransactionRequest.category_name : "",
                payment_reference: this.mutableTransactionRequest ? this.mutableTransactionRequest.payment_reference : "",
                amount: this.mutableTransactionRequest ? this.mutableTransactionRequest.amount : null,
                vat_type: this.mutableTransactionRequest ? this.mutableTransactionRequest.vat_type : defaultVatRate,
                includes_vat: true,
                vat: this.mutableTransactionRequest ? this.mutableTransactionRequest.vat : null,
                total: this.mutableTransactionRequest ? this.mutableTransactionRequest.total : null,
                bill_to: this.mutableTransactionRequest ? this.mutableTransactionRequest.bill_to : "landlord",
                recover_from_tenant: this.mutableTransactionRequest ? this.mutableTransactionRequest.recover_from_tenant : false,
                effective_date: this.mutableTransactionRequest ? this.mutableTransactionRequest.effective_date : this.$dayjs().format("YYYY-MM-DD"),
                description: this.mutableTransactionRequest ? this.mutableTransactionRequest.description : "",
                status: this.mutableTransactionRequest ? this.mutableTransactionRequest.status : "new",
                supporting_documents: this.mutableTransactionRequest ? this.mutableTransactionRequest.supporting_documents : [],
                account_number: this.mutableTransactionRequest ? this.mutableTransactionRequest.account_number : null,
                beneficiary_details: this.mutableTransactionRequest ? this.mutableTransactionRequest.beneficiary_details : null
            });

            if (this.ticketId) {
                this.form.ticket_id = this.ticketId;
            }

            if (this.inspectionId) {
                this.form.inspection_id = this.inspectionId;
            }

            this.$refs.documentDropzone.setOption("autoProcessQueue", false);
        },

        closeForm () {
            this.$emit("close");
        },

        editOrCreate () {
            if (this.mutableTransactionRequest) {
                this.editTransactionRequest();
            } else {
                this.createTransactionRequest();
            }
        },

        fetchBeneficiaries (search) {
            axios.get(`/transaction-requests/${this.assetId}/beneficiaries/search`, {
                params: {
                    filter: search,
                    per_page: 50
                }
            }).then(response => {
                this.beneficiarySearchResults = response.data.data;
            }).catch(errors => {
                toastr.error("Could not load rams beneficiaries. " + errors.message);
            });
        },

        bankIconPath (bankName) {
            switch (bankName) {
                case "ABSA":
                    return "/images/bank_icons/ABSA.png";
                case "First National Bank":
                    return "/images/bank_icons/FNB.png";
                case "Nedbank":
                    return "/images/bank_icons/Nedbank.png";
                case "Standard Bank":
                    return "/images/bank_icons/Standard Bank.png";
                case "Capitec":
                    return "/images/bank_icons/Capitec.png";
                case "Investec":
                    return "/images/bank_icons/Investec.png";
                default:
                    return null;

            }
        },

        editTransactionRequest () {
            App.postMultipartForm(`/transaction-requests/update/${this.mutableTransactionRequest.id}`, this.form)
            .then(response => {
                this.$emit("update-transaction-request-list", response);
                this.closeForm();
                toastr.success("Successfully Updated Billing Request");
            }).catch(error => {
                toastr.error("Unable to update billing request. " + error.message);
                this.handleArchivingError(error.message);
            });
        },

        createTransactionRequest () {
            App.postMultipartForm(`/transaction-requests/store`, this.form)
            .then(response => {
                this.$emit("update-transaction-request-list", response);
                this.closeForm();
                toastr.success("Successfully Created Billing Request");
            }).catch(error => {
                toastr.error("Unable to create billing request. " + error.message);
                this.handleArchivingError(error.message);
            });
        },

        handleArchivingError (errorMessage) {
            if (errorMessage.includes("The associated lease has been archived or deleted")) {
                this.$emit("archive-contact-group");
                this.closeForm();
            }
        },

        showDropzoneError (files, error, xhr) {
            if (error.message !== "Upload cancelled.") {
                toastr.error(error.message);
            }
        },

        fileAdded (file) {
            this.form.supporting_documents = [];

            if (this.form.supporting_documents.length >= this.documentUploadLimit) {
                toastr.error("File limit reached! Only 1 document per request allowed");
            } else {
                let document = {};
                document.uuid = file.upload.uuid;
                document.name = file.name;
                document.progress = 0;
                document.type = file.type;
                document.size = file.size;
                document.source = file;
                document.from_existing_media = false;
                this.form.supporting_documents.push(document);
            }
        },

        removeFile (file, requestFromDropzone = true) {
            if (requestFromDropzone) {
                this.$refs.documentDropzone.removeFile(file);
            } else {
                if (this.$refs.documentDropzone.getAddedFiles().length > 0) {
                    this.$refs.documentDropzone.removeFile(file.source);
                }

                let index = this.form.supporting_documents.indexOf(file);
                if (index > -1) {
                    this.form.supporting_documents.splice(index, 1);
                }
            }
        },

        dragOver (event) {
            // Add some visual fluff to show the user can drop its files
            if (!event.currentTarget.children[0].childNodes[0].classList.contains("border-dashed")) {
                event.currentTarget.children[0].childNodes[0].classList.add("bg-blue-50", "border-dashed", "border-gray-400");
            }
        },

        dragLeave (event) {
            // Clean up
            event.currentTarget.children[0].childNodes[0].classList.remove("bg-blue-50", "border-dashed", "border-gray-400");
        },

        multiUploadUrl () {
            return this.ticketId ? "/attachments/multi/ticket/" + this.ticketId : "/attachments/multi/inspection/" + this.inspectionId;
        },

        attachSelectedMedia (selectedDocuments) {
            if (!selectedDocuments) {
                toastr.error("Sorry, the selected media could not be attached! Please try again.");
                return;
            }

            if (selectedDocuments.length > this.documentUploadLimit) {
                toastr.error(`Only ${this.documentUploadLimit} document(s) allowed!`);
            }

            this.form.supporting_documents = [];

            let document = selectedDocuments[0];
            document.from_existing_media = true;
            this.form.supporting_documents.push(document);

            this.toggleMultiAttachmentModal(false);
        },

        toggleMultiAttachmentModal (override = null) {
            this.showMultiUploadModal = override !== null ? override : !this.showMultiUploadModal;
        },
    },
};
</script>
